import { requestTextMessageLocation, startSession, caseTransferRequest, checkForActiveSession, checkCaseTransfer, requestMultipleLocationsByTextMessage, startOverwatchSession, startOverwatchWeaSession, requestNewNumberLocations, helpRequestRequestMessage, getActiveTeamSearches, getAllActiveTeamSearches} from '@/services/location-request'
import socket, { createObserverSocket } from '@/services/socket'
import { RootState } from '@/store'
import { ActionContext, Module } from 'vuex'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { SOCKETIO_EVENTS } from '../../../server/src/stream/stream.constants'
import axios from 'axios'
import store from '@/store'
import i18n from '@/i18n'
import authentication, { AuthenticationState, authenticationTypes } from './authentication'
import presenceSocket from '@/services/presence-socket'
import { secondsToHms } from '@/filters/seconds-to-hms'
import { currentUser } from '@/services/authentication'
// import { createImportSpecifier, validateLocaleAndSetLanguage } from 'typescript'

// Twilio mediaUrl for archive upload; never send base64 data URLs.
function resolveArchiveMediaUrl(mediaItem: { mediaUrl?: string; url?: string } | null | undefined): string | undefined {
    const candidates = [mediaItem?.mediaUrl, mediaItem?.url]
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.length > 0 && !candidate.startsWith('data:')) {
            return candidate
        }
    }
    return undefined
}

export interface Location {
    phoneNumber: any
    altitude: string
    address: string
    uncertaintyRadiusInMeters: number
    ipAddress: string
    id: number
    latitude: string
    longitude: string
    capturedFromSource: string
    dateTimeCaptured: string
    dateTimeStored: string
    timestamp: number
    socketId: string
    speed: number
}

export type TeamLocationUpdate = Location & {
    phoneNumber?: string
    targetId?: string
}
export interface Log {
    // Log Message
    message: string
    // Logged time
    time: number
    // Maybe use to store icons
    icon?: string
}

export interface TeamActiveTrack {
    phoneNumber: string
    targetId?: string
    latitude: string | null
    longitude: string | null
    address?: string
    uncertaintyRadiusInMeters?: number
    speed?: number
    altitude?: string
    dateTimeStored?: string
    isSharingLocation?: boolean
}

export interface TeamActiveCase {
    uuid: string
    phoneNumber: string
    fullName: string
    latitude: string | null
    longitude: string | null
    address?: string
    uncertaintyRadiusInMeters?: number
    speed?: number
    altitude?: string
    dateTimeStored?: string
    isSharingLocation?: boolean
    isOverwatch?: boolean
    tracks?: TeamActiveTrack[]
}

export interface TeamLiveDisplayCase extends TeamActiveCase {
    trackKey: string
    targetId?: string
}

function locationFieldsFromUpdate(location: Location) {
    return {
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
        uncertaintyRadiusInMeters: location.uncertaintyRadiusInMeters,
        speed: location.speed,
        altitude: location.altitude,
        dateTimeStored: location.dateTimeStored,
        isSharingLocation: true as boolean,
    }
}

function flattenTeamLiveCases(cases: TeamActiveCase[]): TeamLiveDisplayCase[] {
    const result: TeamLiveDisplayCase[] = []
    for (const activeCase of cases) {
        if (activeCase.isOverwatch) {
            for (const track of activeCase.tracks || []) {
                result.push({
                    ...activeCase,
                    phoneNumber: track.phoneNumber || activeCase.phoneNumber,
                    targetId: track.targetId,
                    latitude: track.latitude,
                    longitude: track.longitude,
                    address: track.address,
                    uncertaintyRadiusInMeters: track.uncertaintyRadiusInMeters,
                    speed: track.speed,
                    altitude: track.altitude,
                    dateTimeStored: track.dateTimeStored,
                    isSharingLocation: track.isSharingLocation,
                    trackKey: `${activeCase.uuid}:${track.phoneNumber || track.targetId || ''}`,
                })
            }
            continue
        }

        result.push({
            ...activeCase,
            trackKey: activeCase.uuid,
        })
    }
    return result
}

export interface LocationState {
    path: string
    connectionRequest: any
    locations: Location[]
    s3Data: any[]
    chatMessages?: any[]
    status: string
    showSmsConfirmation: boolean
    phoneNumber: string
    log: Log[]
    callListNumbers: any
    newNumbersList: any
    observationLinkNumbers: any
    observationLinkEmails: any
    callerConnected: boolean
    observerConnected: boolean
    isTabFocused: boolean
    isSharingLocation: boolean
    isSharingLocationByNumber: any
    isObserver: boolean
    sharedNumbersList: any[]
    connectedNumbersList: any[]
    sharedTargetsList: any[]
    evidenceSubmittedNumbers: string[]
    evidenceSubmissionsByNumber: Record<string, {
        formData: Record<string, unknown> | null
        locationCoords: { latitude: number; longitude: number; accuracy: number } | null
        mediaFileNames: Array<string | { fileName: string; contentType?: string; url?: string }>
    }[]>
    alertPortalInformationByKey: Record<string, {
        name?: string
        phoneNumber?: string
        emergencyDescription?: string
        portalId?: string
    }[]>
    isResponse: boolean
    locationType: string | null
    showWaitingForLocationModal: boolean
    showEmailVerificationModal: boolean
    showClearedDomainModal: boolean
    showEditEmailModal: boolean
    locationRequestError: string | null
    showCallTransferModal: boolean
    showAcceptCallTransferModal: boolean
    showAcceptCallObservationrModal: boolean
    showAcceptTeamTrackCallTransferModal: boolean
    showAcceptOverwatchCallTransferModal: boolean
    teamTrackTransferPhoneNumber: string
    teamTrackTransferUuid: string
    hasActiveSearch: boolean
    showAdminTransferNoticeModal: boolean
    adminTransferIsOverwatch: boolean
    caseRequestUuid: string
    originalStreamUuid: string
    requestStreamUuid: string
    caseTransferStatus: boolean
    caseObservationStatus: boolean
    caseTransferWatcher: boolean
    caseObservationWatcher: boolean
    showCaseTransferModal: boolean
    showAdminCaseTransferModal: boolean
    showFindByNumbersModal: boolean
    showFindByListModal: boolean
    showCreateCallListModal: boolean
    showAddUsersToCLModal: boolean
    showServiceAlertModal: boolean
    showAddNumbersModal: boolean
    showShareCaseBySmsModal: boolean
    showUserProfileModal: boolean
    checkCaseTransfer: boolean
    checkCaseObservation: boolean
    thisSocketToken: string
    caseRequestSocketToken: string
    locationInterval: ReturnType<typeof setInterval> | null
    dateTimeCompleted: boolean
    language: string
    deepLLanguage: string
    deliveryMethod: string
    overwatchMessageType: string
    customSmsText: string
    showA2PMessagingModal: boolean
    showA2PMobileMessagingModal: boolean
    showA2POverwatchModal: boolean
    showEventTimelineModal: boolean
    incidentLog: any
    chatInitiated: boolean
    showAPIUserMetricModal: boolean
    weatherCondition: any
    observerSocket: any | null;
    overwatchGeoWatchId: any
    weaIdentifier: string
    weaLink: string
    teamActiveCases: TeamActiveCase[]
    teamCaseSockets: Record<string, any>
}

const LocationModule: Module<LocationState, RootState> = {
    namespaced: true,
    state: {
        connectionRequest: null,
        path: '',
        s3Data: [],
        chatMessages: [],
        locations: [],
        status: 'Not Connected',
        showSmsConfirmation: false,
        phoneNumber: '',
        log: [],
        callListNumbers: [],
        newNumbersList: [],
        observationLinkNumbers: [],
        observationLinkEmails: [],
        callerConnected: false,
        observerConnected: false,
        isTabFocused: false,
        isSharingLocation: false,
        isSharingLocationByNumber: {},
        isObserver: false,
        sharedNumbersList: [],
        connectedNumbersList: [],
        sharedTargetsList: [],
        evidenceSubmittedNumbers: [],
        evidenceSubmissionsByNumber: {},
        alertPortalInformationByKey: {},
        isResponse: false,
        locationType: null,
        showWaitingForLocationModal: false,
        showEmailVerificationModal: false,
        showClearedDomainModal: false,
        showEditEmailModal: false,
        locationRequestError: null,
        showCallTransferModal: false,
        showFindByNumbersModal: false,
        showFindByListModal: false, 
        showCreateCallListModal: false,
        showAddUsersToCLModal: false,
        showAcceptCallTransferModal: false,
        showAcceptCallObservationrModal: false,
        teamTrackTransferUuid: '',
        hasActiveSearch: false,
        showAdminTransferNoticeModal: false,
        adminTransferIsOverwatch: false,
        showAcceptTeamTrackCallTransferModal: false,
        showAcceptOverwatchCallTransferModal: false,
        teamTrackTransferPhoneNumber: '',
        showServiceAlertModal: true,
        showAddNumbersModal: false,
        showShareCaseBySmsModal: false,
        showUserProfileModal: false,
        caseTransferStatus: false,
        caseObservationStatus: false,
        caseTransferWatcher: false,
        caseObservationWatcher: false,
        caseRequestUuid: '',
        originalStreamUuid: '',
        requestStreamUuid: '',
        showCaseTransferModal: false,
        showAdminCaseTransferModal: false,
        checkCaseTransfer: false,
        checkCaseObservation: false,
        thisSocketToken: '',
        caseRequestSocketToken: '',
        dateTimeCompleted: false,
        locationInterval: null,
        language: 'English',
        deepLLanguage: 'Off',
        deliveryMethod: 'standard',
        overwatchMessageType: 'link',
        customSmsText: '',
        showA2PMessagingModal: false,
        showA2PMobileMessagingModal: false,
        showA2POverwatchModal: false,
        showEventTimelineModal: false,
        incidentLog: [],
        chatInitiated: false,
        showAPIUserMetricModal: false,
        weatherCondition: null,
        observerSocket: null,
        overwatchGeoWatchId: null,
        weaIdentifier: '',
        weaLink: '',
        teamActiveCases: [],
        teamCaseSockets: {},
    },
    mutations: {
        connectionRequest(state, connectionRequest) {
            state.connectionRequest = connectionRequest
        },
        reset(state) {
            state.path = ''
            state.locations = []
            state.status = 'Not Connected'
            state.showSmsConfirmation = false
            state.phoneNumber = ''
            state.log = []
            state.callListNumbers = []
            state.newNumbersList = []
            state.observationLinkNumbers = []
            state.observationLinkEmails = []
            state.callerConnected = false
            state.observerConnected = false
            state.isTabFocused = false
            state.isSharingLocation = false
            state.isSharingLocationByNumber = {}
            state.isObserver = false
            state.sharedNumbersList = []
            state.connectedNumbersList = []
            state.sharedTargetsList = []
            state.evidenceSubmittedNumbers = []
            state.evidenceSubmissionsByNumber = {}
            state.alertPortalInformationByKey = {}
            state.isResponse = false
            state.locationType = null
            state.showWaitingForLocationModal = false
            state.showEmailVerificationModal = false
            state.showClearedDomainModal = false
            state.showEditEmailModal = false
            state.locationRequestError = null
            state.showCallTransferModal = false
            state.showFindByNumbersModal = false
            state.showFindByListModal = false
            state.showCreateCallListModal = false
            state.teamTrackTransferUuid = ''
            state.showAddUsersToCLModal = false
            state.showAcceptCallTransferModal = false
            state.showAcceptCallObservationrModal = false
            state.showAcceptTeamTrackCallTransferModal = false
            state.showAcceptOverwatchCallTransferModal = false
            state.teamTrackTransferPhoneNumber = ''
            state.showServiceAlertModal = true
            state.showAddNumbersModal = false
            state.showShareCaseBySmsModal = false
            state.showUserProfileModal =false
            state.caseTransferStatus = false
            state.caseObservationStatus = false
            state.caseTransferWatcher = false
            state.hasActiveSearch = false
            state.showAdminTransferNoticeModal = false
            state.adminTransferIsOverwatch = false
            state.caseObservationWatcher = false
            state.caseRequestUuid = ''
            state.originalStreamUuid = ''
            state.requestStreamUuid = ''
            state.showCaseTransferModal = false
            state.showAdminCaseTransferModal = false
            state.checkCaseTransfer = false
            state.checkCaseObservation = false
            state.thisSocketToken = ''
            state.caseRequestSocketToken = ''
            state.dateTimeCompleted = false
            state.language = 'English'
            state.deepLLanguage = 'Off'
            state.deliveryMethod = 'standard'
            state.overwatchMessageType = 'link'
            state.customSmsText = ''
            clearInterval(state.locationInterval)
            state.showA2PMessagingModal = false
            state.showA2PMobileMessagingModal = false
            state.showA2POverwatchModal =false
            state.showEventTimelineModal = false
            state.incidentLog = []
            state.chatInitiated = false
            state.showAPIUserMetricModal = false
            state.weatherCondition = null
            state.observerSocket = null
            state.overwatchGeoWatchId = null
            Object.values(state.teamCaseSockets).forEach((s: any) => s.disconnect())
            state.teamCaseSockets = {}
            state.teamActiveCases = []
        },
        teamActiveCases(state, cases: TeamActiveCase[]) {
            state.teamActiveCases = cases
        },
        setTeamCaseSocket(state, { uuid, socket }: { uuid: string; socket: any }) {
            state.teamCaseSockets = { ...state.teamCaseSockets, [uuid]: socket }
        },
        resetTeamLive(state) {
            Object.values(state.teamCaseSockets).forEach((s: any) => s.disconnect())
            state.teamCaseSockets = {}
            state.teamActiveCases = []
        },
        addLocation(state, location: Location) {
            state.locations.push(location)
            state.s3Data.push(location)
        },
        addS3Data(state, location: Location) {
            state.s3Data.push(location)
        },
        showSmsConfirmation(state, show: boolean) {
            state.showSmsConfirmation = show
        },
        phoneNumber(state, phoneNumber: string) {
            state.phoneNumber = phoneNumber
        },
        overwatchGeoWatchId(state, value) {
            state.overwatchGeoWatchId = value
        },
        weaIdentifier(state, value) {
            state.weaIdentifier = value
        },
        weaLink(state, value) {
            state.weaLink = value
        },
        path(state, path) {
            state.path = path
        },
        status(state, newStatus: string) {
            state.status = newStatus
        },
        log(state, entry: string) {
            state.log.push({
                message: entry,
                time: Date.now()
            })
        },
        callList(state, callList: any) {
            state.callListNumbers = callList
        },
        newNumbers(state, newNumbers: any) {
            state.newNumbersList = newNumbers
        },
        observationLinkNumbers(state, numbers: any) {
            state.observationLinkNumbers = numbers
        },
        observationLinkEmails(state, emails: any) {
            state.observationLinkEmails = emails
        },
        callerConnected(state, isConnected: boolean) {
            state.callerConnected = isConnected
        },
        observerConnected(state, isConnected: boolean) {
            state.observerConnected = isConnected
        },
        isTabFocused(state, value: boolean) {
            state.isTabFocused = value
        },
        isSharingLocation(state, value: boolean) {
            state.isSharingLocation = value
        },
        isSharingLocationByNumber(state, value: any) {
            state.isSharingLocationByNumber = value
        },
        isObserver(state, value: boolean) {
            state.isObserver = value
        },
        sharedNumbersList(state, sharedNumbersList: any) {
            state.sharedNumbersList = sharedNumbersList
        },
        connectedNumbersList(state, connectedNumbersList: any) {
            state.connectedNumbersList = connectedNumbersList
        },
        sharedTargetsList(state, sharedTargetsList: any) {
            state.sharedTargetsList = sharedTargetsList
        },
        addEvidenceSubmittedNumber(state, phoneNumber: string) {
            if (phoneNumber && !state.evidenceSubmittedNumbers.includes(phoneNumber)) {
                state.evidenceSubmittedNumbers.push(phoneNumber)
            }
        },
        isResponse(state, value: boolean) {
            state.isResponse = value
        },
        locationType(state, value: string) {
            state.locationType = value
        },
        showWaitingForLocationModal(state, value: boolean) {
            state.showWaitingForLocationModal = value
        },
        showEmailVerificationModal(state, value: boolean) {
            state.showEmailVerificationModal = value
        },
        showClearedDomainModal(state, value: boolean) {
            state.showClearedDomainModal = value
        },
        showEditEmailModal(state, value: boolean) {
            state.showEditEmailModal = value
        },
        locationRequestError(state, value: string | null) {
            state.locationRequestError = value
        },
        showCallTransferModal(state, value:boolean | null){
            state.showCallTransferModal = value
        },
        showAcceptCallTransferModal(state, value:boolean | null){
            state.showAcceptCallTransferModal = value
        },
        teamTrackTransferPhoneNumber(state, value: string) {
            state.teamTrackTransferPhoneNumber = value
        },
        teamTrackTransferUuid(state, value: string) {    // add
            state.teamTrackTransferUuid = value
        },
        hasActiveSearch(state, value: boolean) {         // add
            state.hasActiveSearch = value
        },
        showAdminTransferNoticeModal(state, value: boolean) {
            state.showAdminTransferNoticeModal = value
        },
        adminTransferIsOverwatch(state, value: boolean) {
            state.adminTransferIsOverwatch = value
        },
        showAcceptCallObservationrModal(state, value:boolean | null) {
            state.showAcceptCallObservationrModal = value
        },
        showAcceptTeamTrackCallTransferModal(state, value:boolean | null) {
            state.showAcceptTeamTrackCallTransferModal = value
        },
        showAcceptOverwatchCallTransferModal(state, value:boolean | null) {
            state.showAcceptOverwatchCallTransferModal = value
        },
        showFindByNumbersModal(state, value:boolean | null){
            state.showFindByNumbersModal = value
        },
        showFindByListModal(state, value:boolean | null){
            state.showFindByListModal = value
        },
        showCreateCallListModal(state, value:boolean | null){
            state.showFindByListModal = value
        },
        showServiceAlertModal(state, value:boolean | null){
            state.showServiceAlertModal = value
        },
        showAddNumbersModal(state) {
            state.showAddNumbersModal = !state.showAddNumbersModal
        },
        showShareCaseBySmsModal(state, value: boolean) {
            state.showShareCaseBySmsModal = value
        },
        showAddUsersToCLModal(state, value:boolean | null){
            state.showAddUsersToCLModal = value
        },
        showCaseTransferModal(state, value:boolean | null){
            state.showCaseTransferModal = value
        },
        showAdminCaseTransferModal(state, value:boolean | null){
            state.showAdminCaseTransferModal = value
        },
        showUserProfileModal(state, show: boolean) {
            state.showUserProfileModal = show
        },
        changeCaseRequestUuid(state, value: string | null){
            state.caseRequestUuid = value
        },
        caseRequestSocketToken(state, value: string) {
            state.caseRequestSocketToken = value
        },
        addPhoneNumber(state, value: string | null){
            state.phoneNumber = value
            console.log(value)
        },
        setLanguage(state, value:string){
            state.language = value
        },
        setDeepLLanguage(state, value:string){
            if(value == 'English'){
                state.deepLLanguage = 'Off' //default to no translation required
            }
            else if (value == 'Spanish'){
                state.deepLLanguage = 'es'
            }
            else if (value == 'Portuguese'){
                state.deepLLanguage = 'pt-BR'
            }
            else if (value == 'French'){
                state.deepLLanguage = 'fr'
            }
            else if (value == 'German'){
                state.deepLLanguage = 'de'
            }
            else if (value == 'Italian'){
                state.deepLLanguage = 'it'
            }
            else if (value == 'Mandarin'){
                state.deepLLanguage = 'zh'
            }
            else if (value == 'Arabic'){
                state.deepLLanguage = 'ar'
            }
            else if (value == 'Japanese'){
                state.deepLLanguage = 'ja'
            }
        },
        setDeliveryMethod(state, value: string) {
            state.deliveryMethod = value
        },
        setOverwatchMessageType(state, value: string) {
            state.overwatchMessageType = value
        },
        setCustomSmsText(state, value: string) {
            state.customSmsText = value
        },
        showA2PMessagingModal(state, value: boolean){
            state.showA2PMessagingModal = value
        },
        showA2PMobileMessagingModal(state, value: boolean){
            state.showA2PMobileMessagingModal = value
        },
        showA2POverwatchModal(state, value: boolean){
            state.showA2POverwatchModal = value
        },
        showEventTimelineModal(state, value: boolean) {
            state.showEventTimelineModal = value
        },
        incidentLog(state, value: any[]) {
            state.incidentLog = value
        },
        chatInitiated(state, value:boolean | null){
            state.chatInitiated = value
        },
        showAPIUserMetricModal(state, value:boolean){
            state.showAPIUserMetricModal = value
        },
        saveWeatherCondition(state, value:any){
            state.weatherCondition = value
        },
        setChatMessages(state, messages: any[]) {
            state.chatMessages = messages
        },
        observerSocket(state, socketInstance) {
            state.observerSocket = socketInstance
        },
        observerCleanup(state) {
            state.locations = []
            state.s3Data = []
            state.log = []
            state.status = 'Not Connected'
            state.showSmsConfirmation = false
            state.showWaitingForLocationModal = false
            state.isSharingLocation = false
            state.isTabFocused = false
            state.observerConnected = false
            state.isObserver = false
            state.sharedNumbersList = []
            state.connectedNumbersList = []
            state.sharedTargetsList = []
            state.evidenceSubmittedNumbers = []
            state.evidenceSubmissionsByNumber = {}
            state.alertPortalInformationByKey = {}
            state.showA2PMessagingModal = false
            state.showA2PMobileMessagingModal = false
            state.showA2POverwatchModal = false
            state.showEventTimelineModal = false
            state.chatInitiated = false
            state.observerSocket = null
        },
        updateTeamCaseLocation(state, { uuid, location }: { uuid: string; location: TeamLocationUpdate }) {
            const index = state.teamActiveCases.findIndex(x => x.uuid === uuid)
            if (index < 0) return

            const activeCase = state.teamActiveCases[index]
            const fields = locationFieldsFromUpdate(location)

            if (activeCase.isOverwatch) {
                const trackId = location.phoneNumber || location.targetId || activeCase.phoneNumber
                const tracks = [...(activeCase.tracks || [])]
                const trackIndex = tracks.findIndex(track =>
                    (location.phoneNumber && track.phoneNumber === location.phoneNumber) ||
                    (location.targetId && track.targetId === location.targetId) ||
                    (!location.phoneNumber && !location.targetId && track.phoneNumber === activeCase.phoneNumber)
                )
                const nextTrack: TeamActiveTrack = {
                    ...(trackIndex >= 0 ? tracks[trackIndex] : {}),
                    ...fields,
                    phoneNumber: location.phoneNumber || (trackIndex >= 0 ? tracks[trackIndex].phoneNumber : trackId),
                    targetId: location.targetId || (trackIndex >= 0 ? tracks[trackIndex].targetId : undefined),
                }
                if (trackIndex >= 0) {
                    tracks[trackIndex] = nextTrack
                } else {
                    tracks.push(nextTrack)
                }

                state.teamActiveCases.splice(index, 1, {
                    ...activeCase,
                    ...fields,
                    tracks,
                    isSharingLocation: tracks.some(track => track.isSharingLocation),
                })
                return
            }

            state.teamActiveCases.splice(index, 1, {
                ...activeCase,
                ...fields,
            })
        },
        updateTeamCaseSharing(state, { uuid, isSharingLocation }: { uuid: string; isSharingLocation: boolean }) {
            const index = state.teamActiveCases.findIndex(x => x.uuid === uuid)
            if (index < 0) return

            const activeCase = state.teamActiveCases[index]
            if (activeCase.isOverwatch) return

            state.teamActiveCases.splice(index, 1, {
                ...activeCase,
                isSharingLocation,
                ...(isSharingLocation ? {} : {
                    latitude: null,
                    longitude: null,
                    address: undefined,
                    uncertaintyRadiusInMeters: undefined,
                    speed: undefined,
                    altitude: undefined,
                    dateTimeStored: undefined,
                }),
            })
        },
        updateTeamCaseTrackSharing(state, { uuid, identifier, isSharingLocation }: { uuid: string; identifier: string; isSharingLocation: boolean }) {
            const index = state.teamActiveCases.findIndex(x => x.uuid === uuid)
            if (index < 0 || !identifier) return

            const activeCase = state.teamActiveCases[index]
            const tracks = [...(activeCase.tracks || [])]
            const trackIndex = tracks.findIndex(track => track.phoneNumber === identifier || track.targetId === identifier)

            if (trackIndex < 0) {
                if (!isSharingLocation) return
                tracks.push({
                    phoneNumber: identifier,
                    latitude: null,
                    longitude: null,
                    isSharingLocation: true,
                })
            } else {
                tracks[trackIndex] = {
                    ...tracks[trackIndex],
                    isSharingLocation,
                    ...(isSharingLocation ? {} : {
                        latitude: null,
                        longitude: null,
                        address: undefined,
                        uncertaintyRadiusInMeters: undefined,
                        speed: undefined,
                        altitude: undefined,
                        dateTimeStored: undefined,
                    }),
                }
            }

            state.teamActiveCases.splice(index, 1, {
                ...activeCase,
                tracks,
                isSharingLocation: tracks.some(track => track.isSharingLocation),
            })
        },
        addEvidenceSubmission(state, payload: {
            portalId?: string
            phoneNumber?: string
            formData?: Record<string, unknown> | null
            locationCoords?: { latitude: number; longitude: number; accuracy: number } | null
            mediaFileNames?: Array<string | { fileName: string; contentType?: string; url?: string }>
        }) {
            const keys = [...new Set([payload.portalId, payload.phoneNumber].filter(Boolean))] as string[]
            if (!keys.length) return
            const entry = {
                formData: payload.formData ?? null,
                locationCoords: payload.locationCoords ?? null,
                mediaFileNames: Array.isArray(payload.mediaFileNames) ? payload.mediaFileNames : [],
            }
            keys.forEach((key) => {
                if (!state.evidenceSubmittedNumbers.includes(key)) {
                    state.evidenceSubmittedNumbers.push(key)
                }
                if (!state.evidenceSubmissionsByNumber[key]) {
                    state.evidenceSubmissionsByNumber[key] = []
                }
                state.evidenceSubmissionsByNumber[key].push(entry)
            })
        },
        addAlertPortalInformation(state, payload: {
            name?: string
            phoneNumber?: string
            emergencyDescription?: string
            portalId?: string
        }) {
            const key = payload.portalId || payload.phoneNumber
            if (!key) return
            if (!state.alertPortalInformationByKey[key]) {
                state.alertPortalInformationByKey[key] = []
            }
            state.alertPortalInformationByKey[key].push({
                name: payload.name,
                phoneNumber: payload.phoneNumber,
                emergencyDescription: payload.emergencyDescription,
                portalId: payload.portalId,
            })
        },
    },
    getters: {
        teamLiveDisplayCases(state): TeamLiveDisplayCase[] {
            return flattenTeamLiveCases(state.teamActiveCases)
        },
    },
    actions: {
        //depreciated -> should always fall to sms request
        //session is made within this function no matter what
        async requestLocation({ dispatch, commit, state, rootState }): Promise<any> {
            //if the first char is a +, convert phone value to international
            let phone = state.phoneNumber
            if (state.phoneNumber[0] == "+"){
                phone = `${state.phoneNumber}`
            }
            else{
                phone = `+1${state.phoneNumber}`
            }
            commit('log', i18n.t('dashboard.log.requestedLocationFor', { phone }))

            // Session is created first. Automatic lookup is no longer available, so confirm SMS.
            try {
                const response = await startSession({ phoneNumber: phone })

                if (response.data.token) {
                    commit('connectionRequest', response.data)
                    await dispatch('startConnection', response.data.token)
                }
            } catch (e) {
                // @ts-ignore
                if (e.response && e.response.data.error) {
                    // @ts-ignore
                    const message = e.response.data.error
                    if (message === 'verify-email') return commit('showEmailVerificationModal', true)
                    if (message === 'unauthorized-domain') return commit('showClearedDomainModal', true)
                }
            }

            commit('showSmsConfirmation', true)
            commit('log', i18n.t('dashboard.log.unableToAutoRetrieveLocation'))
            commit('status', 'Unable to automatically retrieve location')
        },
        async requestMultipleLocations({ dispatch, commit, state }) {
            // Will need to create a version of this for multiple numbers
            const response = await startOverwatchSession({ phoneNumbers: state.callListNumbers })
            commit('incidentLog', [...state.incidentLog, {
                time: Date.now(),
                message: i18n.t('dashboard.log.searchInitiated'),
                autoLog: true }
            ])

            if (response.data.token) {
                commit('connectionRequest', response.data)
                await dispatch('startConnection', response.data.token)
            }

            // Initiate request SMS send
            requestMultipleLocationsByTextMessage({
                connectionRequestUuid: state.connectionRequest.uuid,
                callList: state.callListNumbers,
                language: state.language,
                messageType: state.overwatchMessageType,
                customSms: state.customSmsText,
                deliveryMethod: state.deliveryMethod,
            })
        },
        requestNewNumberLocations({ dispatch, state, commit }) {
            // Update callListNumbers in the state with newNumbersList
            const updatedCallListNumbers = [...state.callListNumbers, ...state.newNumbersList]
            commit('callList', updatedCallListNumbers)
            commit('incidentLog', [...state.incidentLog, {
                time: Date.now(),
                message: i18n.t('dashboard.log.additionalNumbersRequestInitiated'),
                autoLog: true }
            ])
        
            requestNewNumberLocations({
                connectionRequestUuid: state.connectionRequest.uuid,
                newNumbers: state.newNumbersList,
                language: state.language,
                messageType: state.overwatchMessageType,
                customSms: state.customSmsText,
                deliveryMethod: state.deliveryMethod,
            })
        },
        async helpRequestRequestMessage({ state }, number) {
            helpRequestRequestMessage({
                connectionRequestUuid: state.connectionRequest.uuid,
                phoneNumber: number,
                language: state.language
            })
        },
        async requestLocationSms({ dispatch, commit, state }): Promise<any> {
            //if the first char is a +, convert phone value to international
            let phone = state.phoneNumber
            if (state.phoneNumber[0] == "+"){
                    phone = `${state.phoneNumber}`
            }
            else{
                phone = `+1${state.phoneNumber}`
            }
            commit('log', i18n.t('dashboard.log.requestedSmsLocationFor', { phone }))
            try {
                // Send text message location request.
                const response = await requestTextMessageLocation({
                    connectionRequestUuid: state.connectionRequest.uuid,
                    language: state.language,
                    deliveryMethod: state.deliveryMethod
                })
                if (response.data.token) {
                    commit('log', i18n.t('dashboard.log.requestForSmsLocationSent'))
                    commit('locationType', 'Text')
                    commit('showWaitingForLocationModal', true)
                    commit('incidentLog', [...state.incidentLog, {
                        time: Date.now(),
                        message: i18n.t('dashboard.log.searchInitiated'),
                        autoLog: true }
                    ])
                    return
                }

                commit('log', i18n.t('dashboard.log.unableToRequestSmsLocation'))
                commit('status', 'Unable to send SMS request for location')
            } catch (e) {
                commit('log', i18n.t('dashboard.log.unableToRequestSmsLocation'))
                commit('status', 'Unable to send SMS request for location')
                // @ts-ignore
                if (e.response && e.response.data.error) {
                    // @ts-ignore
                    const message = e.response.data.error
                    if (message === 'verify-email') return commit('showEmailVerificationModal', true)
                    if (message === 'unauthorized-domain') return commit('showClearedDomainModal', true)
                }
            }
        },
        // Initialize the socket connection
        startConnection({ dispatch, commit, state }, token: string) {
            state.caseTransferWatcher = true
            state.caseObservationWatcher = true
            state.thisSocketToken = token
            commit('log', i18n.t('dashboard.log.connectingToServer'))
            commit('hasActiveSearch', true)
            dispatch('disconnectPresence')
            socket.connect({ token })
            // axios.post(`api/v1/location/save-connect-socket/${state.connectionRequest.uuid}`)
            //     .then((response) => {
            //     })

            const onLocationAvailable = (location: Location) => {
                // Don't add location if it's in the store
                const isLocationInStore = state.locations.find(
                    (existingLocation) => existingLocation.dateTimeCaptured === location.dateTimeCaptured
                )

                // Hide if reconnecting and locations come in.
                commit('showWaitingForLocationModal', false)
                commit('showSmsConfirmation', false)

                if (isLocationInStore) return

                if (location.capturedFromSource === 'TEXT_MESSAGE') commit('locationType', 'Text')
                if (location.capturedFromSource === 'RAPID_SOS') commit('locationType', 'RapidSOS')

                // For dispatcher reconnection while caller is already connected.
                //TODO: Upon reconnecting, all statuses will resolve as true, where not necessarily true..
                if (!state.callerConnected) {
                    commit('log', i18n.t('dashboard.log.callerConnected'))
                    commit('callerConnected', true)
                }
                commit('showWaitingForLocationModal', false)
                commit('log', i18n.t('dashboard.log.newLocationReceived'))
                commit('isSharingLocation', true) //Not triggered under some conditions
                commit('isTabFocused', true) //Not triggered under some conditions*/
                if (location.phoneNumber) {
                    const connectedMap = new Map(state.connectedNumbersList.map(status => [status[0], status[1]]))
                    connectedMap.set(location.phoneNumber, true)
                    commit('connectedNumbersList', Array.from(connectedMap.entries()))
                }
                commit('addLocation', location)
            }
            socket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE, onLocationAvailable)
            socket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE_API, onLocationAvailable)

            socket.on('connect', (location: Location) => {
                commit('log', i18n.t('dashboard.log.connectedToServer'))
                commit('status', 'Connected')
            })

            socket.on(SOCKETIO_EVENTS.CALLER_CONNECTED, (isConnected: Boolean) => {
                if (isConnected) {
                    commit('log', i18n.t('dashboard.log.callerConnected'))
                    commit('callerConnected', isConnected)
                    commit('incidentLog', [...state.incidentLog, {
                        time: Date.now(),
                        message: i18n.t('dashboard.log.personInDistressSharedLocation'),
                        autoLog: true }
                    ])
                } else {
                    commit('log', i18n.t('dashboard.log.callerDisconnected'))
                    commit('callerConnected', false)
                    commit('isTabFocused', false)
                    commit('isSharingLocation', false)
                    commit('isSharingLocationByNumber', [false, state.phoneNumber])
                }
            })

            socket.on(SOCKETIO_EVENTS.OBSERVER_CONNECTED, (isConnected: Boolean) => {
                const watching = !!isConnected
                if (state.observerConnected === watching) return
                commit('observerConnected', watching)
                commit('log', i18n.t(watching ? 'dashboard.log.observerConnected' : 'dashboard.log.observerDisconnected'))
                commit('incidentLog', [...state.incidentLog, { 
                    time: Date.now(), 
                    message: i18n.t(watching ? 'dashboard.log.caseObservationInitiated' : 'dashboard.log.caseObservationEnded'),
                    autoLog: true }
                ])
            })

            socket.on(SOCKETIO_EVENTS.IS_TAB_FOCUSED, (value: Boolean) => {
                commit('isTabFocused', value)
            })

            socket.on(SOCKETIO_EVENTS.IS_TAB_FOCUSED_BY_NUMBER, (value: any) => {
                const updateMap = new Map(state.connectedNumbersList.map(connection => [connection[0], connection[1]]));
                updateMap.set(value[0], value[1]);
                commit('connectedNumbersList', Array.from(updateMap.entries()));

                if (!value[1]) {
                    const phoneNumber = value[0]
                    setTimeout(() => {
                        const sharingMap = new Map(state.sharedNumbersList.map(status => [status[0], status[1]]))
                        sharingMap.set(phoneNumber, false)
                        commit('sharedNumbersList', Array.from(sharingMap.entries()))
                    }, 500)
                }
            })

            socket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION, (value: Boolean) => {
                commit('isSharingLocation', value)
                if(value === false) {
                    commit('log', i18n.t('dashboard.log.callerStoppedSharing'))
                }
            })

            socket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, (value: any) => {
                // value = [phoneNumber, true/false]
                const updateMap = new Map(state.sharedNumbersList.map(status => [status[0], status[1]]));
                updateMap.set(value[0], value[1]);
                commit('sharedNumbersList', Array.from(updateMap.entries()));
            })

            socket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, (value: any) => {
                // value = [targetId, true/false]
                const updateMap = new Map(state.sharedTargetsList.map(status => [status[0], status[1]]));
                updateMap.set(value[0], value[1]);
                commit('sharedTargetsList', Array.from(updateMap.entries()));
            })

            socket.on(SOCKETIO_EVENTS.EVIDENCE_SUBMITTED_BY_NUMBER, (data: string | {
                portalId?: string
                phoneNumber?: string
                formData?: Record<string, unknown> | null
                locationCoords?: { latitude: number; longitude: number; accuracy: number } | null
                mediaFileNames?: Array<string | { fileName: string; contentType?: string; url?: string }>
            }) => {
                if (typeof data === 'string') {
                    commit('addEvidenceSubmission', { phoneNumber: data })
                    commit('incidentLog', [...state.incidentLog, {
                        time: Date.now(),
                        message: i18n.t('dashboard.log.pidSharingEvidence'),
                        autoLog: true }
                    ])
                    return
                }
                if (data?.portalId || data?.phoneNumber) {
                    commit('addEvidenceSubmission', data)
                    commit('incidentLog', [...state.incidentLog, {
                        time: Date.now(),
                        message: i18n.t('dashboard.log.pidSharingEvidence'),
                        autoLog: true }
                    ])
                }
            })

            socket.on(SOCKETIO_EVENTS.ALERT_PORTAL_INFORMATION_SUBMITTED, (data: {
                name?: string
                phoneNumber?: string
                emergencyDescription?: string
                portalId?: string
            }) => {
                if (data?.portalId || data?.phoneNumber) {
                    commit('addAlertPortalInformation', data)
                    commit('incidentLog', [...state.incidentLog, {
                        time: Date.now(),
                        message: i18n.t('dashboard.log.emergencyAlertShared'),
                        autoLog: true }
                    ])
                }
            })

            socket.on(SOCKETIO_EVENTS.LOCATION_SHARING_ERROR, (data: any) => {
                commit('log', i18n.t('dashboard.log.callerUnableToShareLocation', { data }))
            })

            socket.on(SOCKETIO_EVENTS.TEAMTRACK_CASE_TRANSFER_REQUESTED, (data: { phoneNumber?: string }) => {
                if (data?.phoneNumber) {
                    commit('teamTrackTransferPhoneNumber', data.phoneNumber)
                }
                state.caseTransferStatus = true
                if (!state.showAcceptTeamTrackCallTransferModal) {
                    commit('showAcceptTeamTrackCallTransferModal', true)
                }
            })

            socket.on(SOCKETIO_EVENTS.OVERWATCH_CASE_TRANSFER_REQUESTED, (data: { phoneNumber?: string }) => {
                if (data?.phoneNumber) {
                    commit('teamTrackTransferPhoneNumber', data.phoneNumber)
                }
                state.caseTransferStatus = true
                if (!state.showAcceptOverwatchCallTransferModal) {
                    commit('showAcceptOverwatchCallTransferModal', true)
                }
            })

            socket.on('connect_timeout', (location: Location) => commit('status', 'connect_timeout'))
            socket.on('disconnect', (location: Location) => commit('status', 'Disconnected'))
            socket.on('reconnect_attempt', (location: Location) => commit('status', 'Attempting to Reconnect'))
            socket.on('reconnect', (location: Location) => commit('status', 'reconnect'))
            socket.on('error', (location: Location) => commit('status', 'error'))
            socket.on('reconnect_error', (location: Location) => commit('status', 'Attempting to Reconnect'))
            socket.on('reconnect_failed', (location: Location) => commit('status', 'Unable to Reconnect'))
        },
        setDateTimeCompleted({state}) {
            const uuid = state.caseRequestUuid || state.connectionRequest?.uuid
            if(uuid){
                axios.post(`api/v1/location/set-date-time-completed/${uuid}`)
            }
        },
        closeConnection({ commit, state }, payload) {
            /*axios.get(`api/v1/location/get-date-time-completed/${state.connectionRequest.uuid}`)
            .then((response) => {
                state.dateTimeCompleted = response.data
            })
            if (state.caseTransferStatus == false){
                //close normally
                socket.close()
                commit('log', i18n.t('dashboard.log.connectionClosed'))
            }
            else if (state.caseTransferStatus == true && state.dateTimeCompleted == false){
                //close if it was apart of a CT (first dispatcher)
                axios.post(`api/v1/location/participant-disconnect`)
                socket.disconnect() //socket is still open
            }
            else{
                //close if it was apart of CT (last dispatcher)
                socket.close()
            }*/
            socket.close()
            commit('log', i18n.t('dashboard.log.connectionClosed'))
        },
        reset({ dispatch, commit, state }) {
            if (state.observerSocket) {
                state.observerSocket.disconnectPermanently('observation ended')
            }
            dispatch('setDateTimeCompleted')
            dispatch('closeConnection')
            commit('showWaitingForLocationModal', false)
            commit('reset')
            dispatch('connectPresence')
        },
        observationReset({ commit, state }) {
            if (state.observerSocket) {
                state.observerSocket.disconnectPermanently('observation ended')
                state.observerSocket = null
            }
            commit('observerCleanup')
        },
        async subscribeToTeamLive({ dispatch, commit, rootState }) {
            const teamId = rootState.authentication.teams[0]?.id
            const isAdmin = store.state.authentication.currentUser.isAdmin
            const { data } = isAdmin
                ? await getAllActiveTeamSearches()
                : await getActiveTeamSearches(String(teamId))

            commit('teamActiveCases', data.map((uniqueCase: TeamActiveCase) => ({
                ...uniqueCase,
                latitude: null,
                longitude: null,
                isSharingLocation: false,
                isOverwatch: uniqueCase.isOverwatch === true || Number(uniqueCase.isOverwatch) === 1,
                tracks: uniqueCase.isOverwatch === true || Number(uniqueCase.isOverwatch) === 1 ? [] : undefined,
            })))
            for (const uniqueCase of data) {
                await dispatch('connectTeamCaseSocket', uniqueCase.uuid)
            }
        },
        async connectTeamCaseSocket({ commit, state }, uuid: string) {
            if (state.teamCaseSockets[uuid]) return

            const { data: token } = await axios.get(`/api/v1/location/get-connect-token/${uuid}`)
            const caseSocket = createObserverSocket(token)
            const onTeamLocation = (location: TeamLocationUpdate) => {
                commit('updateTeamCaseLocation', { uuid, location })
            }
            caseSocket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE, onTeamLocation)
            caseSocket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE_API, onTeamLocation)
            caseSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION, (value: Boolean) => {
                commit('updateTeamCaseSharing', { uuid, isSharingLocation: !!value })
            })
            caseSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, (value: any) => {
                const identifier = Array.isArray(value) ? value[0] : value
                const isSharingLocation = Array.isArray(value) ? !!value[1] : !!value
                commit('updateTeamCaseTrackSharing', { uuid, identifier, isSharingLocation })
            })
            caseSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, (value: any) => {
                const identifier = Array.isArray(value) ? value[0] : value
                const isSharingLocation = Array.isArray(value) ? !!value[1] : !!value
                commit('updateTeamCaseTrackSharing', { uuid, identifier, isSharingLocation })
            })
            commit('setTeamCaseSocket', { uuid, socket: caseSocket })
        },
        disconnectTeamLive({ commit }) {
            commit('resetTeamLive')
        },
        downloadLocations({ state }) {
            const content = new Blob([JSON.stringify(state.locations)], { type: 'text/plain;charset=utf-8' })
            const fileName = `${state.phoneNumber}_${state.locations[0].dateTimeCaptured}.json`
            saveAs(content, fileName)
        },
        async downloadChatConvo({ state }, messages) {
            const json = Object.values(messages)
            let CSV = ''
            const keys = (json[0] && Object.keys(json[0])) || []

            const reversedKeys = Object.keys(json[0])
            const reversedValues = keys.map((key) => json.map((line) => line[key]))

            CSV += reversedKeys.join(',') + '\n'

            for (let i = 0; i < json.length; i++) {
                CSV += reversedValues.map((values) => values[i]).join(',') + '\n'
            }

            const content = new Blob([CSV], { type: 'text/plain;charset=utf-8' })
            const fileName = `SMS Chat-${new Date().toLocaleDateString()}-${state.phoneNumber}.csv`
            saveAs(content, fileName)
        },
        async downloadEventTimeLine({ state }) {
            const json = Object.values(state.incidentLog || {}).map((entry: any) => {
                const { autoLog, time, ...rest } = entry || {}
                return { time: typeof time === 'number' ? secondsToHms(time) : time,...rest}
            })
            let CSV = ''
            const keys = (json[0] && Object.keys(json[0])) || []
            const reversedValues = keys.map((key) => json.map((line) => line[key]))

            CSV += keys.join(',') + '\n'

            for (let i = 0; i < json.length; i++) {
                CSV += reversedValues.map((values) => values[i]).join(',') + '\n'
            }

            const content = new Blob([CSV], { type: 'text/plain;charset=utf-8' })
            const fileName = `EventLog-${new Date().toLocaleDateString()}-${state.phoneNumber}.csv`
            saveAs(content, fileName)
        },
        async prepareEventUpload({ state }) {
            return Object.values(state.incidentLog || {}).map((entry: any) => {
                const { autoLog, time, ...rest } = entry || {}
                return { time: typeof time === 'number' ? secondsToHms(time) : time,...rest}
            })
        },
        async uploadToS3({ state, dispatch }) {
            const locations = state.s3Data.map((location) => ({
                Dispatcher: store.state.authentication.currentUser.firstName + ' ' + store.state.authentication.currentUser.lastName,
                PhoneNumber: state.phoneNumber,
                Longitude: location.longitude,
                Latitude: location.latitude,
                UncertaintyRadiusInMeters: location.uncertaintyRadiusInMeters.toFixed(2),
                Altitude: location.altitude.toFixed(4),
                Address: location.address,
                CapturedFromSource: location.capturedFromSource,
                CapturedAt: new Date(location.dateTimeCaptured).toUTCString()
            }))

            const chats = (state.chatMessages || []).map((message: any) => {
                const messageTime = message.author === 'Dispatcher'
                    ? { SentAt: message.SentAt }
                    : { ReceivedAt: message.ReceivedAt }

                const mediaItems = Array.isArray(message.media)
                    ? message.media.map((mediaItem: any, index: number) => {
                        const baseId =
                            mediaItem.uuid ||
                            mediaItem.id ||
                            `${message.id || 'msg'}-${index}`
                        const timestampPart =
                            mediaItem.timestamp ||
                            message.SentAt ||
                            message.ReceivedAt ||
                            new Date().toISOString()
                        const mediaId = `${baseId}-${timestampPart}`
                        const mimeType = mediaItem.contentType || mediaItem.mimeType || mediaItem.type || ''
                        const inferredExt =
                            (typeof mimeType === 'string' && mimeType.includes('/'))
                                ? mimeType.split('/')[1]
                                : ''
                        const extension = mediaItem.extension || inferredExt || 'bin'
                        const fileName = `media/${mediaId}.${extension}`
                        const archiveUrl = resolveArchiveMediaUrl(mediaItem)

                        return {
                            MediaId: mediaId,
                            Type: mediaItem.mediaType || mediaItem.kind || 'Media',
                            FileName: fileName,
                            Url: archiveUrl,
                            ContentType: mimeType
                        }
                    })
                    : []

                return {
                    PhoneNumber: state.phoneNumber,
                    Dispatcher: store.state.authentication.currentUser.firstName + ' ' + store.state.authentication.currentUser.lastName,
                    Author: message.author,
                    Text: message.text,
                    ...messageTime,
                    Media: mediaItems
                }
            })

            const flattenedMedia = chats.flatMap((chat: any) => chat.Media || [])
            const teamId = store.state.authentication.teams[0].id
            const timelineLog = await dispatch('prepareEventUpload')
            await axios.post(`/api/v1/aws/upload-to-s3/${teamId}/${state.connectionRequest.uuid}`, {
                locations,
                chats,
                timelineLog,
                mediaItems: flattenedMedia
                    .filter((m: any) => m.Url)
                    .map((m: any) => ({
                        url: m.Url,
                        contentType: m.ContentType,
                        fileName: m.FileName
                    }))
            })
        },
        async uploadOverwatchToS3({ state, dispatch }) {
            const locations = state.s3Data.map((location) => {
                const sourceData = location.sourceData && typeof location.sourceData === 'object' ? location.sourceData : {}
                const phoneNumber = location.phoneNumber || sourceData.phoneNumber
                const targetId = location.targetId || sourceData.targetId
                return {
                    Dispatcher: store.state.authentication.currentUser.firstName + ' ' + store.state.authentication.currentUser.lastName,
                    PhoneNumber: phoneNumber,
                    targetId,
                    Longitude: location.longitude,
                    Latitude: location.latitude,
                    UncertaintyRadiusInMeters: location.uncertaintyRadiusInMeters.toFixed(2),
                    Altitude: location.altitude.toFixed(4),
                    Address: location.address,
                    CapturedFromSource: location.capturedFromSource,
                    CapturedAt: new Date(location.dateTimeCaptured).toUTCString()
                }
            })

            const chats = (state.chatMessages || []).map((message: any) => {
                const messageTime = message.author === 'Dispatcher'
                    ? { SentAt: message.SentAt }
                    : { ReceivedAt: message.ReceivedAt }

                const mediaItems = Array.isArray(message.media)
                    ? message.media.map((mediaItem: any, index: number) => {
                        const baseId =
                            mediaItem.uuid ||
                            mediaItem.id ||
                            `${message.id || 'msg'}-${index}`
                        const timestampPart =
                            mediaItem.timestamp ||
                            message.SentAt ||
                            message.ReceivedAt ||
                            new Date().toISOString()
                        const mediaId = `${baseId}-${timestampPart}`
                        const mimeType = mediaItem.contentType || mediaItem.mimeType || mediaItem.type || ''
                        const inferredExt =
                            (typeof mimeType === 'string' && mimeType.includes('/'))
                                ? mimeType.split('/')[1]
                                : ''
                        const extension = mediaItem.extension || inferredExt || 'bin'
                        const fileName = `media/${mediaId}.${extension}`
                        const archiveUrl = resolveArchiveMediaUrl(mediaItem)

                        return {
                            MediaId: mediaId,
                            Type: mediaItem.mediaType || mediaItem.kind || 'Media',
                            FileName: fileName,
                            Url: archiveUrl,
                            ContentType: mimeType
                        }
                    })
                    : []

                return {
                    PhoneNumber: message.phoneNumber,
                    Dispatcher: store.state.authentication.currentUser.firstName + ' ' + store.state.authentication.currentUser.lastName,
                    Author: message.author,
                    Text: message.text,
                    ...messageTime,
                    Media: mediaItems
                }
            })

            const flattenedMedia = chats.flatMap((chat: any) => chat.Media || [])
            const teamId = store.state.authentication.teams[0].id
            const timelineLog = await dispatch('prepareEventUpload')
            await axios.post(`/api/v1/aws/upload-overwatch-to-s3/${teamId}/${state.connectionRequest.uuid}`, {
                locations,
                chats,
                timelineLog,
                mediaItems: flattenedMedia
                    .filter((m: any) => m.Url)
                    .map((m: any) => ({
                        url: m.Url,
                        contentType: m.ContentType,
                        fileName: m.FileName
                    }))
            })
        },
        async acceptCaseTransfer({dispatch, commit, state}) {
            commit('log', i18n.t('dashboard.log.acceptedCallTransferRequest'))
            axios.post(`api/v1/location/mark-case-transfer-confirmation/${state.connectionRequest.uuid}/+1${state.phoneNumber}/${state.thisSocketToken}`)
            commit('reset')
        },
        async denyCaseTransfer({dispatch, commit, state}){
            commit('log', i18n.t('dashboard.log.deniedCallTransferRequest'))
            axios.post(`api/v1/location/mark-transfer/${state.connectionRequest.uuid}/+1${state.phoneNumber}/${0}`)
            .then((response) => {});
            state.caseTransferStatus = false
        },
        async checkTransfer({dispatch, commit, state}) {
            let phone = state.phoneNumber
            if (state.phoneNumber[0] == "+") phone = `${state.phoneNumber}`
            else phone = `+1${state.phoneNumber}`

            if (state.caseTransferWatcher)  {
                axios.get(`api/v1/location/check-transfer/${state.connectionRequest.uuid}/${phone}`)
                .then((response) => {
                    let caseTransfer = response.data
                    if (caseTransfer == true) {
                        state.caseTransferStatus = true
                        if (!state.showAcceptCallTransferModal) {
                            commit('showAcceptCallTransferrModal', true)
                        }
                    } else {
                        state.caseTransferStatus = false
                    }
                })
            }
        },
        async findOverwatchCaseTransfer({ commit, state }) {
            const uuid = state.connectionRequest.uuid
            for (const number of state.callListNumbers) {
                const { data } = await axios.get(`api/v1/location/find-overwatch-case-transfer/${uuid}/${encodeURIComponent(number)}`)
                if (data === true) {
                    commit('teamTrackTransferPhoneNumber', number)
                    state.caseTransferStatus = true
                    if (!state.showAcceptOverwatchCallTransferModal) {
                        commit('showAcceptOverwatchCallTransferModal', true)
                    }
                    return
                }
            }
        },
        async confirmOverwatchCaseTransfer({ commit, state }, value) {
            const uuid = state.connectionRequest.uuid
            const phoneNumber = state.teamTrackTransferPhoneNumber
            await axios.post('api/v1/location/confirm-overwatch-case-transfer', {
                uuid,
                phoneNumber,
                value,
            })
            // parsing to remove numbers from sidebar
            const digits = (n: string) => String(n || '').replace(/\D/g, '')
            const transferred = digits(phoneNumber)
            if (value === '1' && transferred) {
                commit('callList', state.callListNumbers.filter(n => digits(n) !== transferred))
            }
            state.caseTransferStatus = false
            commit('teamTrackTransferPhoneNumber', '')
        },
        async findTeamTrackCaseTransfer({ commit, state }) {
            const uuid = state.connectionRequest.uuid
            for (const number of state.callListNumbers) {
                const { data } = await axios.get(`api/v1/location/find-teamtrack-case-transfer/${uuid}/${encodeURIComponent(number)}`)
                if (data === true) {
                    commit('teamTrackTransferPhoneNumber', number)
                    state.caseTransferStatus = true
                    if (!state.showAcceptTeamTrackCallTransferModal) {
                        commit('showAcceptTeamTrackCallTransferModal', true)
                    }
                    return
                }
            }
            state.caseTransferStatus = false
        },
        async confirmTeamTrackCaseTransfer({ commit, state }, value) {
            const uuid = state.connectionRequest.uuid
            const phoneNumber = state.teamTrackTransferPhoneNumber
            await axios.post(`api/v1/location/confirm-teamtrack-case-transfer`, {
                uuid,
                phoneNumber,
                value,
            })
            if (value === '1') {
                commit('callList', state.callListNumbers.filter(n => n !== phoneNumber))
            }
            state.caseTransferStatus = false
            commit('teamTrackTransferPhoneNumber', '')
        },
        async checkOverwatchTransfer({ commit, dispatch }) {
            const { data } = await axios.get('api/v1/location/check-overwatch-transfer-accepted')
            if (!data?.token) return false

            commit('connectionRequest', data)
            commit('addPhoneNumber', data.transferPhoneNumber)
            commit('callList', data.callListNumbers ?? [data.transferPhoneNumber])
            await dispatch('startConnection', data.token)
            return true
        },
        async checkAdminOverwatchTransfer({ commit, dispatch }) {
            const { data } = await axios.get('api/v1/location/check-teamtrack-transfer-accepted')
            if (!data?.token) return false

            commit('connectionRequest', data)
            commit('addPhoneNumber', data.transferPhoneNumber)
            commit('callList', data.callListNumbers ?? [data.transferPhoneNumber])
            await dispatch('startConnection', data.token)
            return true
        },
        async acceptCaseObservation({dispatch, commit, state}) {
            commit('log', i18n.t('dashboard.log.acceptedCallObservationRequest'))
            axios.post(`api/v1/location/mark-case-observation-confirmation/${state.connectionRequest.uuid}/+1${state.phoneNumber}/${state.thisSocketToken}`)
        },
        async denyCaseObservation({dispatch, commit, state}){
            commit('log', i18n.t('dashboard.log.deniedCallObservationRequest'))
            axios.post(`api/v1/location/mark-observation/${state.connectionRequest.uuid}/+1${state.phoneNumber}/${0}`)
            .then((response) => {});
            state.caseObservationStatus = false
        },
        async checkCaseTransferConfirmation({dispatch, commit, state}){
            let phone = state.phoneNumber
            if (!phone) return
            if (state.phoneNumber[0] == "+") phone = `${state.phoneNumber}`
            else phone = `+1${state.phoneNumber}`

            const response = await axios.get(`api/v1/location/check-case-transfer/${state.caseRequestUuid}/${encodeURIComponent(phone)}`)
            if (response.data)  {
                state.caseRequestSocketToken = response.data
                state.checkCaseTransfer = true
            }
            else {
                state.caseRequestSocketToken = ''
                state.checkCaseTransfer = false
            }
        },
        async checkOverwatchCaseTransferConfirmation({ commit, dispatch, state }) {
            const { data } = await axios.get('api/v1/location/check-overwatch-transfer-accepted')
            if (data?.token) {
                commit('connectionRequest', data)
                commit('addPhoneNumber', data.transferPhoneNumber)
                state.caseRequestSocketToken = data.token
                state.checkCaseTransfer = true
                await dispatch('startConnection', data.token)
            } else {
                state.caseRequestSocketToken = ''
                state.checkCaseTransfer = false
            }
        },
        async checkObservation({dispatch, commit, state}) {
            let phone = state.phoneNumber
            if (state.phoneNumber[0] == "+") phone = `${state.phoneNumber}`
            else phone = `+1${state.phoneNumber}`

            if (state.caseObservationWatcher)  {
                axios.get(`api/v1/location/check-observation/${state.connectionRequest.uuid}/${phone}`)
                .then((response) => {
                    let caseObservation = response.data
                    if (caseObservation == true) {
                        state.caseObservationStatus = true
                        if (!state.showAcceptCallObservationrModal) {
                            commit('showAcceptCallObservationrModal', true)
                        }
                    } else {
                        state.caseObservationStatus = false
                    }
                })
            }
        },
        async checkCaseObservationConfirmation({dispatch, commit, state}){
            let phone = state.phoneNumber
            if (state.phoneNumber[0] == "+") phone = `${state.phoneNumber}`
            else phone = `+1${state.phoneNumber}`

            axios.get(`api/v1/location/check-case-observation/${state.caseRequestUuid}/${phone}`)
            .then((response) => {
                if (response.data)  {
                    state.caseRequestSocketToken = response.data
                    state.checkCaseObservation = true
                }
                else {
                    state.caseRequestSocketToken = ''
                    state.checkCaseObservation = false
                }
            })
        },
        connectObserverWithToken({ commit, state }, socketToken: string) {
            const activeSocket = createObserverSocket(socketToken)
            commit('observerSocket', activeSocket)
            commit('log', i18n.t('dashboard.log.observerConnecting'))

            const onObserverLocation = (location: Location) => {
                const isLocationInStore = state.locations.find(
                    (existingLocation) => existingLocation.dateTimeCaptured === location.dateTimeCaptured
                )
                if (isLocationInStore) return
                if (location.capturedFromSource === 'TEXT_MESSAGE') commit('locationType', 'Text')
                if (location.capturedFromSource === 'RAPID_SOS') commit('locationType', 'RapidSOS')    
                if (!state.callerConnected) {
                    commit('log', i18n.t('dashboard.log.callerConnected'))
                    commit('callerConnected', true)
                }
                commit('showWaitingForLocationModal', false)
                commit('showSmsConfirmation', false)
                commit('log', i18n.t('dashboard.log.newLocationReceived'))
                commit('isSharingLocation', true)
                commit('isTabFocused', true)
                commit('addLocation', location)
            }
            activeSocket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE, onObserverLocation)
            activeSocket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE_API, onObserverLocation)
            activeSocket.on('connect', () => {
                commit('log', i18n.t('dashboard.log.connectedToServer'))
                commit('status', 'Connected')
                activeSocket.emit(SOCKETIO_EVENTS.OBSERVER_CONNECTED, true)
            })
            activeSocket.emit(SOCKETIO_EVENTS.OBSERVER_CONNECTED, true)
            activeSocket.on(SOCKETIO_EVENTS.CALLER_CONNECTED, (isConnected: Boolean) => {
                if (isConnected) {
                    commit('log', i18n.t('dashboard.log.callerConnected'))
                    commit('callerConnected', isConnected)
                } else {
                    commit('log', i18n.t('dashboard.log.callerDisconnected'))
                    commit('callerConnected', false)
                    commit('isTabFocused', false)
                    commit('isSharingLocation', false)
                    commit('isSharingLocationByNumber', [false, state.phoneNumber])
                }
            })
            activeSocket.on(SOCKETIO_EVENTS.IS_TAB_FOCUSED, (value: Boolean) => { commit('isTabFocused', value) })
            activeSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION, (value: Boolean) => {
                commit('isSharingLocation', value)
                if (value === false) commit('log', i18n.t('dashboard.log.callerStoppedSharing'))
            })
            activeSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, (value: any) => {
                const updateMap = new Map(state.sharedNumbersList.map(status => [status[0], status[1]]))
                updateMap.set(value[0], value[1])
                commit('sharedNumbersList', Array.from(updateMap.entries()))
            })
            activeSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, (value: any) => {
                const updateMap = new Map(state.sharedTargetsList.map(status => [status[0], status[1]]))
                updateMap.set(value[0], value[1])
                commit('sharedTargetsList', Array.from(updateMap.entries()))
            })
            activeSocket.on(SOCKETIO_EVENTS.LOCATION_SHARING_ERROR, (data: any) => {
                commit('log', i18n.t('dashboard.log.callerUnableToShareLocation', { data }))
            })
            activeSocket.on('connect_timeout', () => commit('status', 'connect_timeout'))
            activeSocket.on('disconnect', () => commit('status', 'Disconnected'))
            activeSocket.on('reconnect_attempt', () => commit('status', 'Attempting to Reconnect'))
            activeSocket.on('reconnect', () => commit('status', 'reconnect'))
            activeSocket.on('error', () => commit('status', 'error'))
            activeSocket.on('reconnect_error', () => commit('status', 'Attempting to Reconnect'))
            activeSocket.on('reconnect_failed', () => commit('status', 'Unable to Reconnect'))
        },

        async connectToSocket({ dispatch, commit, state }){
            let phone = state.phoneNumber
            if (state.phoneNumber[0] == "+") phone = `${state.phoneNumber}`
            else phone = `+1${state.phoneNumber}`

            axios.post(`/api/v1/location/join-stream/${state.caseRequestUuid}/${phone}`)
            .then((response) => { 
                if (response.data.token) {
                    commit('connectionRequest', response.data)
                    let token = response.data.token
                    let activeSocket;

                    if (state.isObserver) {
                        activeSocket = createObserverSocket(token)
                        commit('observerSocket', activeSocket)
                        commit('log', i18n.t('dashboard.log.observerConnecting'))
                    } else {
                        activeSocket = socket
                        socket.connect({ token })
                    }

                    // The rest stays exactly the same — just use activeSocket instead of socket
                    const onJoinedLocation = (location: Location) => {
                        const isLocationInStore = state.locations.find(
                            (existingLocation) => existingLocation.dateTimeCaptured === location.dateTimeCaptured
                        )
                        if (isLocationInStore) return

                        if (location.capturedFromSource === 'TEXT_MESSAGE') commit('locationType', 'Text')
                        if (location.capturedFromSource === 'RAPID_SOS') commit('locationType', 'RapidSOS')

                        if (!state.callerConnected) {
                            commit('log', i18n.t('dashboard.log.callerConnected'))
                            commit('callerConnected', true)
                        }
                        commit('showWaitingForLocationModal', false)
                        commit('showSmsConfirmation', false)
                        commit('log', i18n.t('dashboard.log.newLocationReceived'))
                        commit('isSharingLocation', true)
                        commit('isTabFocused', true)
                        commit('addLocation', location)
                    }
                    activeSocket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE, onJoinedLocation)
                    activeSocket.on(SOCKETIO_EVENTS.NEW_LOCATION_AVAILABLE_API, onJoinedLocation)

                    activeSocket.on('connect', () => {
                        commit('log', i18n.t('dashboard.log.connectedToServer'))
                        commit('status', 'Connected')
                        if (state.isObserver) {
                            activeSocket.emit(SOCKETIO_EVENTS.OBSERVER_CONNECTED, true)
                        }
                    })

                    activeSocket.on(SOCKETIO_EVENTS.CALLER_CONNECTED, (isConnected: Boolean) => {
                        if (isConnected) {
                            commit('log', i18n.t('dashboard.log.callerConnected'))
                            commit('callerConnected', isConnected)
                        } else {
                            commit('log', i18n.t('dashboard.log.callerDisconnected'))
                            commit('callerConnected', false)
                            commit('isTabFocused', false)
                            commit('isSharingLocation', false)
                            commit('isSharingLocationByNumber', [false, state.phoneNumber])
                        }
                    })

                    activeSocket.on(SOCKETIO_EVENTS.OBSERVER_CONNECTED, (isConnected: Boolean) => {
                        const watching = !!isConnected
                        if (state.observerConnected === watching) return
                        commit('observerConnected', watching)
                        if (!state.isObserver) {
                            commit('log', i18n.t(watching ? 'dashboard.log.observerConnected' : 'dashboard.log.observerDisconnected'))
                        }
                    })

                    activeSocket.on(SOCKETIO_EVENTS.IS_TAB_FOCUSED, (value: Boolean) => {
                        commit('isTabFocused', value)
                    })

                    activeSocket.on(SOCKETIO_EVENTS.IS_TAB_FOCUSED_BY_NUMBER, (value: any) => {
                        const updateMap = new Map(state.connectedNumbersList.map(status => [status[0], status[1]]));
                        updateMap.set(value[0], value[1]);
                        commit('connectedNumbersList', Array.from(updateMap.entries()))

                        if (!value[1]) {
                            const phoneNumber = value[0]
                            setTimeout(() => {
                                const sharingMap = new Map(state.sharedNumbersList.map(status => [status[0], status[1]]))
                                sharingMap.set(phoneNumber, false)
                                commit('sharedNumbersList', Array.from(sharingMap.entries()))
                            }, 500)
                        }
                    })

                    activeSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION, (value: Boolean) => {
                        commit('isSharingLocation', value)
                        if(value === false) {
                            commit('log', i18n.t('dashboard.log.callerStoppedSharing'))
                        }
                    })

                    activeSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, (value: any) => {
                        const updateMap = new Map(state.sharedNumbersList.map(status => [status[0], status[1]]));
                        updateMap.set(value[0], value[1]);
                        commit('sharedNumbersList', Array.from(updateMap.entries()));
                    })

                    activeSocket.on(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, (value: any) => {
                        const updateMap = new Map(state.sharedTargetsList.map(status => [status[0], status[1]]));
                        updateMap.set(value[0], value[1]);
                        commit('sharedTargetsList', Array.from(updateMap.entries()));
                    })

                    activeSocket.on(SOCKETIO_EVENTS.LOCATION_SHARING_ERROR, (data: any) => {
                        commit('log', i18n.t('dashboard.log.callerUnableToShareLocation', { data }))
                    })

                    activeSocket.on('connect_timeout', () => commit('status', 'connect_timeout'))
                    activeSocket.on('disconnect', () => commit('status', 'Disconnected'))
                    activeSocket.on('reconnect_attempt', () => commit('status', 'Attempting to Reconnect'))
                    activeSocket.on('reconnect', () => commit('status', 'reconnect'))
                    activeSocket.on('error', () => commit('status', 'error'))
                    activeSocket.on('reconnect_error', () => commit('status', 'Attempting to Reconnect'))
                    activeSocket.on('reconnect_failed', () => commit('status', 'Unable to Reconnect'))
                }
            })
        },

        async endObservationSession({dispatch, state}, socketId?: string) {
            axios.post(`api/v1/location/end-observation-session/${state.caseRequestUuid}`, { socketId })
            .then((response => {
                dispatch('observationReset')
            }))
        },

        async emitLatestLocation({dispatch, commit, state}, location: Location){
            axios.get(`api/v1/location/emit-latest-location/${state.caseRequestUuid}/${location}`)
        },

        //Print down the location and check for addLocation function if working

        async getLatestLocations({dispatch, commit, state}){
            axios.get(`api/v1/location/get-latest-location/:${state.connectionRequest.id}`)
            .then((response) => {
                commit('log', response.data.latitude)
                dispatch('emitLatestLocation', response.data)
                commit('addLocation', response.data)
            })
        },

        async latestLocation({state}){
            return state.locations[state.locations.length - 1]
            //pops off the last location of 
        },
        async chatInitiated({state}){
            if(state.chatInitiated) axios.post(`api/v1/location/chat-initiated/${state.connectionRequest.uuid}`) 
        },
        sendMobilebservationLink({state}) {
            const observationData = {
                connectionRequestUuid: state.connectionRequest.uuid,
                observationLinkNumbers: state.observationLinkNumbers,
                language: state.language,
            }
            axios.post(`api/v1/location/send-mobile-observation-sms`, observationData)
        },
        getObservationLink({ state }) {
            return axios.post(`api/v1/location/get-observation-link`, {
                connectionRequestUuid: state.connectionRequest.uuid,
                language: state.language,
            })
        },
        sendMobileObservationEmail({state}) {
            const observationData = {
                connectionRequestUuid: state.connectionRequest.uuid,
                observationLinkEmails: state.observationLinkEmails,
                language: state.language,
            }
            axios.post(`api/v1/location/send-mobile-observation-email`, observationData)
        },
        uploadEvidenceFile(_ctx, payload: { token?: string; teamId?: any; portalId?: string; locationCoords: unknown; formData: unknown; mediaFiles: File[] }) {
            const form = new FormData()
            if (payload.token) form.append('token', payload.token)
            form.append('metadata', JSON.stringify({
                locationCoords: payload.locationCoords,
                formData: payload.formData,
                portalId: payload.portalId,
            }))
            payload.mediaFiles.forEach((file) => form.append('media', file, file.name))
            axios.post('api/v1/aws/upload-evidence-file', form)
        },
        async downloadEvidenceFile(_ctx, payload: { teamId: number | string; uuid: string; firstName?: string; lastName?: string }) {
            const { teamId, uuid, firstName, lastName } = payload
            const { data } = await axios.post(
                `/api/v1/aws/download-evidence-file/${teamId}/${encodeURIComponent(uuid)}`,
                { firstName, lastName },
                { responseType: 'blob' },
            )
            const content = new Blob([data], { type: 'application/zip' })
            const nameForFile = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Evidence'
            saveAs(content, `Evidence_${nameForFile}.zip`)
        },
        async saveLastWeather({state}){
            await axios.post(`api/v1/location/save-latest-weather`, {
                connectionRequestUuid: state.connectionRequest.uuid,
                weatherCondition: state.weatherCondition
            })
        },
        async triggerAIChatSummary({state}){
            try {
                const chats = (state.chatMessages || []).map((message: any) => ({
                    Author: message.author,
                    Text: message.text,
                    Dispatcher: store.state.authentication.currentUser.firstName + ' ' + store.state.authentication.currentUser.lastName,
                    ...(message.author === 'Dispatcher' ? { SentAt: message.SentAt } : { ReceivedAt: message.ReceivedAt })
                }))
                await axios.post('api/v1/location/upload-to-ai-chat-summary', {
                    connectionRequestUuid: state.connectionRequest.uuid,
                    chatlog: chats,
                })
            } catch (error) {
                console.error('AI chat summary error:', error)
            }
        },
        async submitAlertPortalInformation({ dispatch }, payload: {
            token: string
            name?: string
            phoneNumber?: string
            emergencyDescription?: string
            portalId?: string
        }) {
            const { token, name, phoneNumber, emergencyDescription, portalId } = payload
            if (!token) return

            let normalizedPhone = ''
            if (phoneNumber?.trim()) {
                normalizedPhone = decodeURIComponent(phoneNumber)
                normalizedPhone = normalizedPhone.replace(/^1(?=\d{10}$)/, '')
                if (!normalizedPhone.startsWith('+')) {
                    normalizedPhone = '+1' + normalizedPhone
                }
            }

            await dispatch('startConnection', token)

            socket.emit(SOCKETIO_EVENTS.ALERT_PORTAL_INFORMATION_SUBMITTED, {
                name: name?.trim() || undefined,
                phoneNumber: normalizedPhone || undefined,
                emergencyDescription: emergencyDescription?.trim() || undefined,
                portalId: portalId || undefined,
            })

            if (normalizedPhone) {
                socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, [normalizedPhone, true])
            }
        },
        async shareEvidenceLocation({ dispatch, commit, state }, payload: { token: string; phoneNumber: string, portalId: string }) {
            let { token, phoneNumber, portalId } = payload
            if (!token) return

            let normalizedPhone = ''
            if (phoneNumber?.trim()) {
                normalizedPhone = decodeURIComponent(phoneNumber)
                normalizedPhone = normalizedPhone.replace(/^1(?=\d{10}$)/, '')
                if (!normalizedPhone.startsWith('+')) {
                    normalizedPhone = '+1' + normalizedPhone
                }
                if (!state.callListNumbers.includes(normalizedPhone)) {
                    commit('callList', [...state.callListNumbers, normalizedPhone])
                }
            }

            await dispatch('startConnection', token)

            const targetId = normalizedPhone || portalId
            if (normalizedPhone) {
                socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, [normalizedPhone, true])
            }
            if (portalId) {
                socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, [portalId, true])
            }

            const watchID = navigator.geolocation.watchPosition(
                (position) => {
                    const locationPayload = {
                        timestamp: position.timestamp,
                        accuracy: position.coords.accuracy,
                        altitude: position.coords.altitude,
                        altitudeAccuracy: position.coords.altitudeAccuracy,
                        heading: position.coords.heading,
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        speed: position.coords.speed,
                        targetId,
                        phoneNumber: normalizedPhone || undefined,
                    }
                    socket.emit(SOCKETIO_EVENTS.TEXT_MESSAGE_LOCATION_UPDATE, locationPayload)
                    if (normalizedPhone) {
                        socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, [normalizedPhone, true])
                    }
                    if (portalId) {
                        socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, [portalId, true])
                    }
                },
                (error) => {
                    if (normalizedPhone) {
                        socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, [normalizedPhone, false])
                    }
                    if (portalId) {
                        socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, [portalId, false])
                    }
                    socket.emit(SOCKETIO_EVENTS.LOCATION_SHARING_ERROR, error.message)
                },
                { enableHighAccuracy: true }
            )
            // Store the watch ID for later stop capability
            commit('overwatchGeoWatchId', watchID)
        },
        async stopEvidenceLocation({ commit, state }, payload?: { phoneNumber?: string, portalId:string }) {
            const portalId = payload?.portalId
            let phoneNumber = payload?.phoneNumber || state.phoneNumber
            if (phoneNumber?.trim()) {
                phoneNumber = decodeURIComponent(phoneNumber)
                phoneNumber = phoneNumber.replace(/^1(?=\d{10}$)/, '')
                if (!phoneNumber.startsWith('+')) {
                    phoneNumber = '+1' + phoneNumber
                }
                socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_NUMBER, [phoneNumber, false])
            }
            if (portalId) {
                socket.emit(SOCKETIO_EVENTS.IS_SHARING_LOCATION_BY_TARGETID, [portalId, false])
            }
            if (state.overwatchGeoWatchId) {
                navigator.geolocation.clearWatch(state.overwatchGeoWatchId)
                commit('overwatchGeoWatchId', null)
            }
            commit('isSharingLocation', false)
        },
        async createWeaLink({ state, commit, dispatch }, teamId) {
            commit('setOverwatchMessageType', 'alert')
            const weaIdentifier = Math.random().toString(36).slice(2, 17)
            commit('weaIdentifier', weaIdentifier)

            const response = await startOverwatchWeaSession(weaIdentifier)
            if (response.data.token) {
                commit('connectionRequest', response.data)
                await dispatch('startConnection', response.data.token)
            }

            const weaLinkResponse = await axios.post('api/v1/location/request-overwatch-wea-link', {
                connectionRequestUuid: state.connectionRequest.uuid,
                teamId: teamId,
                language: state.language
            })
            commit('weaLink', weaLinkResponse.data)
        },
        async connectPresence({ rootState }) {
            if (store.state.location.hasActiveSearch) return
            const { data: token } = await axios.get('/api/v1/location/get-presence-token')
            presenceSocket.connect(token)
            presenceSocket.on('teamtrack-transfer', (data) => {
                store.dispatch('location/onTeamTrackTransfer', data)
            })

            // Catch refresh / missed events
            const { data: pending } = await axios.get('/api/v1/location/pending-transfer-to-user')
            if (pending?.uuid) {
                store.dispatch('location/onTeamTrackTransfer', {
                    ...pending,
                    dispatcherId: rootState.authentication.currentUser.id,
                })
            }
        },
        disconnectPresence() {
            presenceSocket.disconnect()
        },
        async onTeamTrackTransfer({ commit, rootState }, data) {
            if (data.dispatcherId !== rootState.authentication.currentUser?.id) return
            commit('teamTrackTransferUuid', data.uuid)
            commit('teamTrackTransferPhoneNumber', data.phoneNumber)
            commit('adminTransferIsOverwatch', data.isOverwatch === true)
            if (data.isOverwatch === false) {
                commit('changeCaseRequestUuid', data.uuid)
                commit('addPhoneNumber', data.phoneNumber)
            }
            commit('showAdminTransferNoticeModal', true)
        }
    }
}

export default LocationModule

export interface LocationTypes {
    mutations: {
        'location/connectionRequest': any
        'location/showSmsConfirmation': boolean
        'location/phoneNumber': string
        'location/showEmailVerificationModal': boolean
        'location/showClearedDomainModal': boolean
        'location/showEditEmailModal': boolean
        'location/showCallTransferModal': boolean
        'location/showAcceptCallTransferModal' : boolean
        'location/showAcceptCallObservationrModal': boolean
        'location/showAcceptTeamTrackCallTransferModal': boolean
        'location/showAcceptOverwatchCallTransferModal': boolean
        'location/teamTrackTransferPhoneNumber': string
        'location/teamTrackTransferUuid': string
        'location/hasActiveSearch': boolean
        'location/showAdminTransferNoticeModal': boolean
        'location/adminTransferIsOverwatch': boolean
        'location/showCaseTransferModal': boolean
        'location/showAdminCaseTransferModal': boolean
        'location/showFindByNumbersModal': boolean
        'location/showFindByListModal': boolean
        'location/showCreateCallListModal': boolean
        'location/showAddUsersToCLModal': boolean
        'location/showServiceAlertModal': boolean
        'location/showAddNumbersModal': boolean
        'location/showShareCaseBySmsModal': boolean
        'location/showUserProfileModal': boolean
        'location/changeCaseRequestUuid' : string
        'location/caseRequestSocketToken': string
        'location/addPhoneNumber': string
        'location/callList': any
        'location/callListNumbers': any
        'location/newNumbers' : any
        'location/setLanguage': string
        'location/setDeepLLanguage': string
        'location/setDeliveryMethod': string
        'location/setCustomSmsText': string
        'location/showA2PMessagingModal': boolean
        'location/showA2PMobileMessagingModal': boolean
        'location/showA2POverwatchModal': boolean
        'location/showEventTimelineModal': boolean
        'location/chatInitiated': boolean
        'location/showAPIUserMetricModal': boolean
        'location/isResponse': boolean
        'location/overwatchMessageType': string
        'location/isObserver': boolean
        'location/observerConnected': boolean
        'location/saveWeatherCondition': any
        'location/observationLinkNumbers': any
        'location/observationLinkEmails': any
        'location/setChatMessages': any[]
        'location/incidentLog': any[]
    }
    actions: {
        'location/requestLocation': {}
        'location/requestLocationSms': {}
        'location/reset': {}
        'location/observationReset': {}
        'location/downloadLocations': {}
        'location/acceptCaseTransfer': {}
        'location/confirmTeamTrackCaseTransfer': {}
        'location/findTeamTrackCaseTransfer': {}
        'location/denyCaseTransfer': {}
        'location/requestTransfer' : {}
        'location/checkTransfer' : {}
        'location/acceptCaseObservatoin': {}
        'location/denyCaseObservatoin': {}
        'location/requestObservatoin' : {}
        'location/checkObservatoin' : {}
        'location/checkCaseTransferConfirmation': {}
        'location/checkOverwatchCaseTransferConfirmation': {}
        'location/checkCaseObservationConfirmation': {}
        'location/connectObserverWithToken': {}
        'location/connectToSocket': {}
        'location/requestMultipleLocations': string
        'location/requestNewNumberLocations': string
        'location/helpRequestRequestMessage': {}
        'location/latestLocation': {}
        'location/uploadToS3': {}
        'location/uploadOverwatchToS3': {}
        'location/showA2PMessagingModal': {}
        'location/chatInitiated': {}
        'location/endObservationSession': {}
        'location/sendMobilebservationLink': {}
        'location/sendMobileObservationEmail': {}
        'location/uploadEvidenceFile': {}
        'location/downloadEvidenceFile': {}
        'location/getObservationLink': {}
        'location/downloadChatConvo': {}
        'location/downloadEventTimeLine': {}
        'location/saveLastWeather': {}
        'location/triggerAIChatSummary': {}
        'location/subscribeToTeamLive': {}
        'location/connectTeamCaseSocket': {}
        'location/disconnectTeamLive': {}
        'location/connectPresence': {}
        'location/disconnectPresence': {}
        'location/onTeamTrackTransfer': {}
    }
}
